/**
 * Smart Camera Router
 * 
 * PURPOSE: Single intelligent entry point for ALL camera movement.
 * Automatically selects the correct camera behavior based on geographic context.
 * 
 * DESIGN PRINCIPLES:
 * 1. ALL map movement MUST go through smartCameraMove()
 * 2. No direct map.flyTo(), map.easeTo(), map.jumpTo(), map.fitBounds() outside this file
 * 3. Runtime enforcement blocks direct calls at the map level
 * 4. Every decision is deterministic and testable
 * 
 * ENFORCEMENT:
 * - call installRuntimeEnforcement(map) after map initialization
 * - This monkey-patches map.flyTo/easeTo/jumpTo/fitBounds to throw if called outside router
 * - Use bypassRouter() for legitimate internal calls
 */

import { telemetry } from './utils/telemetry';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// 🛡️ INTERNAL STATE STORAGE — use WeakMap to avoid mutating external map objects.
// This preserves clean architecture by keeping router state separate from library state.
const PENDING_MOVES = new WeakMap<any, SmartCameraOptions>();

export type MovementType = 'flyTo' | 'easeTo' | 'jumpTo' | 'fitBounds';
export type ContextType = 'city' | 'district' | 'neighborhood' | 'street' | 'building' | 'aerial' | 'overview';
export type IntentType = 'explore' | 'navigate' | 'inspect' | 'cinematic' | 'topDown';

export interface CameraProfile {
    zoom: number;
    pitch: number;
    bearing: number;
    speed?: number;
    curve?: number;
}

export interface SmartCameraOptions {
    center?: [number, number];       // [lng, lat] target
    zoom?: number;                    // target zoom (optional, auto-selected)
    pitch?: number;                   // target pitch (optional, auto-selected)
    bearing?: number;                 // target bearing (optional, auto-selected)
    bounds?: any;                     // LngLatBounds (if provided, uses fitBounds)
    intent?: IntentType;              // 'explore' | 'navigate' | 'inspect' | 'cinematic' | 'topDown'
    context?: ContextType;            // 'city' | 'district' | 'neighborhood' | 'street' | 'building' | 'aerial' | 'overview'
    padding?: any;                    // viewport padding (e.g., {right: 400})
    duration?: number;                // optional override
    maxZoom?: number;                 // optional maximum zoom override for bounds fitting
    essential?: boolean;              // bypass reduced-motion
    zoom_delta?: number;              // relative zoom change
}

export interface CameraDecision {
    type: MovementType;
    params: any;
    distance: number;
    context: ContextType;
    intent: IntentType;
    duration: number;
    curve?: number;
    speed?: number;
}

// ============================================================================
// CAMERA INTELLIGENCE CONFIG
// ============================================================================

export const CAMERA_INTELLIGENCE = {
    // Distance thresholds in meters
    thresholds: {
        cinematic: 50000,     // > 50km → flyTo with dramatic curve
        gentle: 1000,         // > 1km → flyTo with gentle curve
        smooth: 100,          // > 100m → easeTo
        instant: 0,           // < 100m → jumpTo
    },

    // Zoom profiles by context
    zoomProfiles: {
        city:        { zoom: 4.5,  pitch: 28, bearing: 0   },
        district:    { zoom: 6.5,  pitch: 30, bearing: 0   },
        neighborhood:{ zoom: 8.5,  pitch: 32, bearing: 0   },
        street:      { zoom: 10,   pitch: 32, bearing: 0   },
        building:    { zoom: 12,   pitch: 32, bearing: 0   },
        aerial:      { zoom: 7.5,  pitch: 30, bearing: 0   },
        overview:    { zoom: 1.2,  pitch: 25, bearing: 0   },
    } as Record<ContextType, CameraProfile>,

    // Pitch presets by intent
    pitchPresets: {
        explore:     { pitch: 28,  bearing: 0   },  // Casual overview
        navigate:    { pitch: 32,  bearing: 0   },  // Standard navigation
        inspect:     { pitch: 32,  bearing: 0   },  // Close inspection
        cinematic:   { pitch: 65,  bearing: -15 },  // 🚀 ATOM D1.6: Dramatic Hollywood Reveal
        topDown:     { pitch: 0,   bearing: 0   },  // Map view
    } as Record<IntentType, { pitch: number; bearing: number }>,

    // Curve defaults by movement type
    curveDefaults: {
        cinematic:   { curve: 1.85, speed: 0.95 },  // 🚀 ATOM D1.6: Dramatic altitude pullback
        gentle:      { curve: 1.2,  speed: 0.8 },   // Less zoom-out, slower
        smooth:      { curve: 1.0,  speed: 1.0 },   // No zoom-out, circular
    },
};

// ============================================================================
// PUBLIC API — Camera Profile
// ============================================================================

/**
 * Get camera profile for a given context.
 * Returns zoom, pitch, bearing defaults.
 */
export function getCameraProfile(context: string): CameraProfile {
    const profile = CAMERA_INTELLIGENCE.zoomProfiles[context as ContextType];
    if (profile) {
        return { ...profile };
    }
    // Return default profile for unknown contexts
    return { zoom: 15.5, pitch: 60, bearing: -20 };
}

// ============================================================================
// PUBLIC API — Camera Selection
// ============================================================================

/**
 * Select the correct camera method based on distance and geometry.
 * 
 * @param distanceMeters - Distance from current position to target in meters
 * @param options - Optional configuration
 * @returns The movement type string
 */
export function selectCamera(
    distanceMeters: number,
    options?: { bounds?: any; returnConfig?: boolean }
): string | any {
    // bounds present → fitBounds (override all)
    if (options?.bounds) {
        if (options?.returnConfig) {
            return { type: 'fitBounds', curve: undefined, speed: undefined };
        }
        return 'fitBounds';
    }

    // > 50km → flyTo (cinematic)
    if (distanceMeters >= CAMERA_INTELLIGENCE.thresholds.cinematic) {
        if (options?.returnConfig) {
            // Hollywood cinematic sweep: Slow speed, high curve, dramatic pitch, random sweeping bearing
            const sweepBearing = (Math.random() > 0.5 ? 1 : -1) * (20 + Math.random() * 40); // sweeps between 20-60 deg
            return { type: 'flyTo', curve: 1.8, speed: 0.6, pitch: 65, bearing: sweepBearing, essential: true };
        }
        return 'flyTo';
    }

    // 1–50km → flyTo (gentle)
    if (distanceMeters >= CAMERA_INTELLIGENCE.thresholds.gentle) {
        if (options?.returnConfig) {
            const sweepBearing = (Math.random() > 0.5 ? 1 : -1) * (10 + Math.random() * 20); // softer sweep
            return { type: 'flyTo', curve: 1.4, speed: 0.8, pitch: 50, bearing: sweepBearing, essential: true };
        }
        return 'flyTo';
    }

    // 100m–1km → easeTo
    if (distanceMeters >= CAMERA_INTELLIGENCE.thresholds.smooth) {
        if (options?.returnConfig) {
            return { type: 'easeTo', curve: undefined, speed: undefined };
        }
        return 'easeTo';
    }

    // < 100m → jumpTo
    if (options?.returnConfig) {
        return { type: 'jumpTo', curve: undefined, speed: undefined };
    }
    return 'jumpTo';
}

// ============================================================================
// PUBLIC API — Duration Calculation
// ============================================================================

/**
 * Calculate animation duration based on geographic distance.
 * 
 * RULES:
 * - Long distance → longer duration (cinematic feel)
 * - Short distance → faster animation (responsiveness)
 * - Clamp between 500ms and 5000ms
 * - Natural logarithmic scaling for smooth transitions
 */
export function calcDuration(distanceMeters: number, options?: {
    minDuration?: number;
    maxDuration?: number;
    baseDistance?: number;
}): number {
    const {
        minDuration = 500,
        maxDuration = 5000,
        baseDistance = 1000,
    } = options || {};

    // Logarithmic scaling: duration grows quickly at first, then plateaus
    const duration = minDuration + 
        (maxDuration - minDuration) * 
        Math.log(1 + distanceMeters / baseDistance) / 
        Math.log(1 + 100000 / baseDistance); // 100km = reference point

    return Math.round(Math.min(maxDuration, Math.max(minDuration, duration)));
}

import { 
    normalizeCoordinate, 
    calculateDistanceHaversine,
    isValidCoordinate,
    coerceCoordinates
} from './coordinateValidator';

const toLngLatArray = (input: any) => normalizeCoordinate(input);

// ============================================================================
// INTERNAL — Coordinate Normalization (REPLACED BY CONTRACT)
// ============================================================================

/**
 * DEPRECATED: Use normalizeCoordinate from utils/coordinateContract
 */
const normalizeCoordinates = (coords: any) => normalizeCoordinate(coords, 'SmartCameraRouter');

// ============================================================================
// PUBLIC API — Haversine Distance (REPLACED BY CONTRACT)
// ============================================================================

/**
 * DEPRECATED: Use calculateDistanceHaversine from utils/coordinateContract
 */
export const haversineDistance = (a: any, b: any) => calculateDistanceHaversine(a, b);


// ============================================================================
// INTERNAL — Core Decision Logic
// ============================================================================

/**
 * Pure logic for deciding camera movement parameters.
 * Does NOT execute any map methods.
 */
function decideCameraMove(map: any, options: SmartCameraOptions): CameraDecision | null {
    const {
        center: rawCenter,
        zoom,
        pitch,
        bearing,
        bounds,
        intent = 'navigate',
        context = 'neighborhood',
        padding,
        duration: durationOverride,
        essential = true,
    } = options;

    const center = normalizeCoordinates(rawCenter);

    if (bounds) {
        const profile = getCameraProfile(context);
        return {
            type: 'fitBounds',
            params: {
                bounds,
                padding: padding || { top: 50, bottom: 50, left: 50, right: 400 },
                maxZoom: options.maxZoom ?? (profile.zoom + 1),
                pitch: pitch ?? profile.pitch,
                bearing: bearing ?? profile.bearing,
                duration: durationOverride || 2500,
                essential,
            },
            distance: 0,
            context,
            intent,
            duration: durationOverride || 2500,
        };
    }


    if (!center && (zoom === undefined && options.zoom_delta === undefined && pitch === undefined && bearing === undefined)) {
        telemetry.error('SMART_CAMERA', 'INVALID_INPUT', { center, options });
        return null;
    }

    // ── Compute distance from current position ──
    const current = map.getCenter();
    const currentZoom = map.getZoom();
    const distance = center ? haversineDistance(
        { lng: current.lng, lat: current.lat },
        { lng: center[0], lat: center[1] }
    ) : 0;

    // 🛡️ JITTER GUARD: Drop moves < 10 meters to prevent oscillations
    if (distance < 10 && !bounds && zoom === undefined && options.zoom_delta === undefined && pitch === undefined && bearing === undefined) {
        telemetry.debug('SMART_CAMERA', 'JITTER_GUARD', { distance });
        return null;
    }

    // ── Select zoom/pitch profile ──
    const zoomProfile = getCameraProfile(context);
    const pitchPreset = CAMERA_INTELLIGENCE.pitchPresets[intent] || CAMERA_INTELLIGENCE.pitchPresets.navigate;

    // ── Calculate Target Zoom ──
    let targetZoom = zoom;
    if (options.zoom_delta !== undefined) {
        targetZoom = currentZoom + options.zoom_delta;
    } else if (targetZoom === undefined) {
        targetZoom = zoomProfile.zoom;
    }

    const targetPitch = pitch ?? pitchPreset.pitch;
    const targetBearing = bearing ?? pitchPreset.bearing;

    // ── Select movement type ──
    const movementType = selectCamera(distance) as MovementType;

    // ── Compute duration ──
    const computedDuration = durationOverride ?? calcDuration(distance, {
        minDuration: movementType === 'jumpTo' ? 0 : 500,
        maxDuration: movementType === 'easeTo' ? 2000 : 5000,
    });

    // ── Build decision ──
    const decision: CameraDecision = {
        type: movementType,
        params: {
            center: center || [current.lng, current.lat],
            zoom: targetZoom,
            pitch: targetPitch,
            bearing: targetBearing,
            duration: computedDuration,
            essential,
        },
        distance,
        context,
        intent,
        duration: computedDuration,
    };

    if (movementType === 'flyTo') {
        const isCinematic = intent === 'cinematic';
        const curveConfig = isCinematic
            ? CAMERA_INTELLIGENCE.curveDefaults.cinematic
            : (distance >= CAMERA_INTELLIGENCE.thresholds.cinematic
                ? CAMERA_INTELLIGENCE.curveDefaults.cinematic
                : CAMERA_INTELLIGENCE.curveDefaults.gentle);
        
        decision.curve = curveConfig.curve;
        decision.speed = curveConfig.speed;
        decision.params.curve = curveConfig.curve;
        decision.params.speed = curveConfig.speed;

        // 🚀 ATOM D1.6: Hollywood Bearing Interpolation
        // Create a sweeping rotational arc based on travel distance/intent.
        if (isCinematic) {
            const travelBearing = (current.lng < center![0]) ? -25 : 25;
            decision.params.bearing = targetBearing + travelBearing;
        }
    }

    return decision;
}

// ============================================================================
// INTERNAL — Core Execution Logic
// ============================================================================

/**
 * Pure execution of a camera decision.
 * Bypasses all guards and logic — only for internal use or via executeCameraDirectly.
 */
function applyCameraDecision(map: any, decision: CameraDecision) {
    logCameraDecision(decision);

    const { type, params } = decision;
    const linearEasing = (t: number) => t;

    switch (type) {
        case 'fitBounds':
            bypassRouter(map, 'fitBounds', params.bounds, params);
            break;
        case 'flyTo':
            // 🚀 ATOM D1.6: Hollywood Easing (Slow-In / Fast-Middle / Soft-Arrival)
            // Replaces robotic linear motion with cinematic momentum.
            params.easing = (t: number) => {
                const isCinematic = decision.intent === 'cinematic';
                if (!isCinematic) return t; // Keep standard linear for navigation
                
                // Cubic-Bezier approximation for drone-like movement
                return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
            };
            bypassRouter(map, 'flyTo', params);
            break;
        case 'easeTo':
            params.easing = linearEasing;
            bypassRouter(map, 'easeTo', params);
            break;
        case 'jumpTo':
            // jumpTo has no duration/easing — strip them for clarity
            delete params.duration;
            delete params.essential;
            bypassRouter(map, 'jumpTo', params);
            break;
        default:
            telemetry.error('SMART_CAMERA', 'UNKNOWN_MOVEMENT_TYPE', { type });
    }
}

// ============================================================================
// PUBLIC API — Direct Camera Movement
// ============================================================================

/**
 * Execute camera movement directly, bypassing style guards and retry logic.
 * 
 * 🛡️ ENFORCEMENT: Still runs logic and enforcement — just skips the "waiting for
 * style" layer. Use this when you KNOW the style is ready or inside a listener.
 */
export function executeCameraDirectly(
    map: any,
    options: SmartCameraOptions
): CameraDecision | void {
    const decision = decideCameraMove(map, options);
    if (decision) {
        applyCameraDecision(map, decision);
        return decision;
    }
}

// ============================================================================
// PUBLIC API — Smart Camera Router (THE ONLY ENTRY POINT)
// ============================================================================

export function smartCameraMove(
    map: any,
    options: SmartCameraOptions
): CameraDecision | 'deferred' | void {
    if (!map) {
        telemetry.error('SMART_CAMERA', 'MISSING_MAP_INSTANCE');
        return;
    }

    // Direct execution: Mapbox camera transforms (flyTo, easeTo, jumpTo) operate directly
    // on the camera projection matrix without requiring all style layers/sprites to be idle.
    return executeCameraDirectly(map, options);
}

// ============================================================================
// INTERNAL — Logging
// ============================================================================

function logCameraDecision(decision: CameraDecision) {
    if (decision.type === 'flyTo') {
        console.log('[SPATIAL] FlyTo requested');
        console.log('[SPATIAL] Duration', decision.duration);
        console.log('[SPATIAL] Pitch', decision.params?.pitch);
        console.log('[SPATIAL] Bearing', decision.params?.bearing);
    }

    const stats = getCameraStats();
    stats.calls += 1;
    stats.lastMethod = decision.type;
    stats.lastDecision = decision;
    stats.lastDecisionAt = Date.now();
    stats.byMethod[decision.type] = (stats.byMethod[decision.type] || 0) + 1;

    const icon = decision.type === 'flyTo' ? '✈️' :
                 decision.type === 'easeTo' ? '➡️' :
                 decision.type === 'jumpTo' ? '⚡' :
                 decision.type === 'fitBounds' ? '🔲' : '❓';

    telemetry.forensic('SMART_CAMERA', 'DECISION', {
        type: decision.type,
        icon,
        distance: decision.distance,
        context: decision.context,
        intent: decision.intent,
        duration: decision.duration,
        curve: decision.curve,
        speed: decision.speed
    });

    telemetry.trackPerf('cameraUpdateCount', 1);

    if (typeof window !== 'undefined') {
        (window as any).__LAST_CAMERA_DECISION__ = decision;
    }
}

// ============================================================================
// PUBLIC API — Camera Stats (observability)
// ============================================================================

export type ViolationSeverity = 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';
export type ViolationType =
    | 'DIRECT_CALL'
    | 'EXTERNAL_ALLOWED'
    | 'STALE_TOKEN'
    | 'MISSING_ORIGINAL'
    | 'INFO';

export interface ViolationRecord {
    type: ViolationType;
    severity: ViolationSeverity;
    method: string;
    at: number;
    stack?: string;
    note?: string;
}

export interface CameraStats {
    calls: number;
    violations: number;
    externalAllowed: number;
    bySeverity: Record<ViolationSeverity, number>;
    byType: Partial<Record<ViolationType, number>>;
    lastMethod: MovementType | null;
    lastDecision: CameraDecision | null;
    lastDecisionAt: number | null;
    lastViolationAt: number | null;
    lastViolationMethod: string | null;
    byMethod: Partial<Record<MovementType, number>>;
    recentViolations: ViolationRecord[];
}

function freshStats(): CameraStats {
    return {
        calls: 0,
        violations: 0,
        externalAllowed: 0,
        bySeverity: { HIGH: 0, MEDIUM: 0, LOW: 0, INFO: 0 },
        byType: {},
        lastMethod: null,
        lastDecision: null,
        lastDecisionAt: null,
        lastViolationAt: null,
        lastViolationMethod: null,
        byMethod: {},
        recentViolations: [],
    };
}

export function recordViolation(v: Omit<ViolationRecord, 'at'> & { at?: number }): void {
    const stats = getCameraStats();
    const record: ViolationRecord = { at: Date.now(), ...v };
    stats.bySeverity[record.severity] = (stats.bySeverity[record.severity] || 0) + 1;
    stats.byType[record.type] = (stats.byType[record.type] || 0) + 1;
    if (record.severity === 'LOW') {
        stats.externalAllowed += 1;
    } else if (record.severity === 'HIGH' || record.severity === 'MEDIUM') {
        stats.violations += 1;
        stats.lastViolationAt = record.at;
        stats.lastViolationMethod = record.method;
    }
    stats.recentViolations.push(record);
    if (stats.recentViolations.length > 50) stats.recentViolations.shift();
}

export function getCameraStats(): CameraStats {
    const w: any = typeof window !== 'undefined' ? window : globalThis;
    if (!w.__CAMERA_STATS__) w.__CAMERA_STATS__ = freshStats();
    return w.__CAMERA_STATS__ as CameraStats;
}

export function resetCameraStats(): void {
    const w: any = typeof window !== 'undefined' ? window : globalThis;
    w.__CAMERA_STATS__ = freshStats();
}

export function getViolations(filter?: {
    severity?: ViolationSeverity | ViolationSeverity[];
    type?: ViolationType | ViolationType[];
    sinceMs?: number;
}): ViolationRecord[] {
    const rows = getCameraStats().recentViolations.slice();
    if (!filter) return rows;
    const sevs = filter.severity ? (Array.isArray(filter.severity) ? filter.severity : [filter.severity]) : null;
    const types = filter.type ? (Array.isArray(filter.type) ? filter.type : [filter.type]) : null;
    const cutoff = filter.sinceMs != null ? Date.now() - filter.sinceMs : null;
    return rows.filter((v) =>
        (!sevs || sevs.includes(v.severity)) &&
        (!types || types.includes(v.type)) &&
        (cutoff == null || v.at >= cutoff)
    );
}

export function assertRouterWasReached(baselineCalls: number, message?: string): void {
    const now = getCameraStats().calls;
    if (now <= baselineCalls) {
        throw new Error(
            `ROUTER_NOT_REACHED: camera stats.calls did not advance (baseline=${baselineCalls}, now=${now}). ` +
            `${message ?? 'Voice/intent path bypassed the Smart Camera Router.'}`
        );
    }
}

// ============================================================================
// EXTERNAL LIBRARY SAFE ACCESS
// ============================================================================

export function safeCameraAccess<T>(callback: () => T): T {
    if (typeof window === 'undefined') {
        return callback();
    }
    const prev = (window as any).__ALLOW_EXTERNAL_CAMERA__;
    (window as any).__ALLOW_EXTERNAL_CAMERA__ = true;
    try {
        return callback();
    } finally {
        (window as any).__ALLOW_EXTERNAL_CAMERA__ = prev ?? false;
    }
}

// ============================================================================
// RUNTIME ENFORCEMENT — Block Direct Mapbox Calls
// ============================================================================

export function installRuntimeEnforcement(
    map: any,
    options?: {
        enabled?: boolean;
        allowInTest?: boolean;
        onViolation?: (method: string, stack: string) => void;
    }
) {
    const { enabled = true, allowInTest = true, onViolation } = options || {};
    if (!map || !enabled) return;

    const isTestMode = typeof window !== 'undefined' && ((window as any).__PLAYWRIGHT__ || (window as any).__JEST__);
    if (isTestMode && allowInTest) return;

    const methodsToBlock = ['flyTo', 'easeTo', 'jumpTo', 'fitBounds'] as const;
    const originals: Record<string, Function> = {};

    for (const method of methodsToBlock) {
        if (typeof map[method] === 'function') {
            originals[method] = map[method].bind(map);
        }
    }

    map.__smartCameraOriginals__ = originals;
    const ENFORCEMENT_MARKER = '__SMART_CAMERA_ENFORCED__';
    if (map[ENFORCEMENT_MARKER]) return;
    map[ENFORCEMENT_MARKER] = true;

    for (const method of methodsToBlock) {
        if (typeof map[method] !== 'function') continue;

        const original = map[method].bind(map);
        map[method] = function (...args: any[]) {
            const isExternalAllowed = typeof window !== 'undefined' && (window as any).__ALLOW_EXTERNAL_CAMERA__ === true;
            if (isExternalAllowed) {
                recordViolation({ type: 'EXTERNAL_ALLOWED', severity: 'LOW', method, note: 'granted via safeCameraAccess' });
                return original(...args);
            }

            const routerActive = typeof window !== 'undefined' && (window as any).__SMART_CAMERA_ROUTER_ACTIVE__ === true;
            const stack = new Error().stack || '';
            const stackHit = stack.includes('smartCameraMove') || stack.includes('bypassRouter');
            const isFromRouter = routerActive || stackHit;

            if (stackHit && !routerActive) {
                recordViolation({ type: 'STALE_TOKEN', severity: 'MEDIUM', method, stack });
            }

            if (!isFromRouter) {
                recordViolation({ type: 'DIRECT_CALL', severity: 'HIGH', method, stack });
                telemetry.error('ENFORCEMENT', 'DIRECT_MAP_CALL_BLOCKED', { method });
                if (onViolation) onViolation(method, stack);
                if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'test') throw new Error(`DIRECT_CALL_BLOCKED: ${method}`);
            }

            return original(...args);
        };
    }
}

/**
 * Bypass runtime enforcement for legitimate internal calls.
 * This is used internally by smartCameraMove() to call map methods.
 */
export function bypassRouter(map: any, method: string, ...args: any[]): any {
    const w: any = typeof window !== 'undefined' ? window : null;
    const prev = w ? w.__SMART_CAMERA_ROUTER_ACTIVE__ : undefined;
    if (w) w.__SMART_CAMERA_ROUTER_ACTIVE__ = true;
    
    try {
        const originals = map.__smartCameraOriginals__;
        const callMethod = (m: string, a: any[]) => {
            if (originals && originals[m]) return originals[m](...a);
            return map[m](...a);
        };

        // 🚀 THE MAVERICK CLEANUP: No more retries. No more loops.
        // We trust the component lifecycle.
        return callMethod(method, args);
    } finally {
        if (w) w.__SMART_CAMERA_ROUTER_ACTIVE__ = prev ?? false;
    }
}

// ============================================================================
// PUBLIC API — Detect Direct Map Calls (for CI/CD)
// ============================================================================

/**
 * Detect direct Mapbox calls that bypass the smart router.
 * This should be run as part of CI/CD or during development.
 */
export function detectDirectMapCalls(sourceCode: string): Array<{
    pattern: string;
    index: number;
    context: string;
}> {
    const patterns = [
        /map\.flyTo\(/g,
        /map\.easeTo\(/g,
        /map\.fitBounds\(/g,
        /map\.jumpTo\(/g,
    ];

    const violations: Array<{ pattern: string; index: number; context: string }> = [];

    for (const pattern of patterns) {
        const matches = sourceCode.matchAll(pattern);
        for (const match of matches) {
            // Check if this is inside smartCameraMove — if not, it's a violation
            const context = sourceCode.substring(
                Math.max(0, match.index - 200),
                match.index + 100
            );
            if (!context.includes('smartCameraMove') && !context.includes('bypassRouter')) {
                violations.push({
                    pattern: pattern.source,
                    index: match.index,
                    context: context.trim(),
                });
            }
        }
    }

    return violations;
}

// ============================================================================
// EXPOSE TO WINDOW
// ============================================================================

if (typeof window !== 'undefined') {
    (window as any).smartCameraMove = smartCameraMove;
    (window as any).executeCameraDirectly = executeCameraDirectly;
    (window as any).selectCamera = selectCamera;
    (window as any).getCameraProfile = getCameraProfile;
    (window as any).calcDuration = calcDuration;
    (window as any).installRuntimeEnforcement = installRuntimeEnforcement;
    (window as any).normalizeCoordinate = normalizeCoordinate;
    (window as any).safeCameraAccess = safeCameraAccess;
    (window as any).getCameraStats = getCameraStats;
    (window as any).resetCameraStats = resetCameraStats;
    (window as any).assertRouterWasReached = assertRouterWasReached;
    (window as any).getViolations = getViolations;
    (window as any).recordViolation = recordViolation;
    (window as any).__CAMERA_INTELLIGENCE__ = CAMERA_INTELLIGENCE;
    // Eager-initialize stats so dashboards can read it before first call
    getCameraStats();
}
